/**
 * table_filters.js
 *
 * Shared logic for table filter forms with debounced text inputs and immediate
 * select/dropdown submission.  Used by both the customers and users pages.
 *
 * Usage:
 *   import { initTableFilters } from '../src/table_filters';
 *   document.addEventListener('turbolinks:load', () => {
 *     initTableFilters({ formId: 'my-filter-form', storageKey: 'my-filter-focus' });
 *
 *     // AJAX mode: updates only the table body and pagination without full page reload
 *     initTableFilters({
 *       formId: 'my-filter-form',
 *       storageKey: 'my-filter-focus',
 *       ajax: true,
 *       targets: ['#filter-results', '#pagination-top', '#pagination-bottom']
 *     });
 *   });
 */

const DEBOUNCE_MS = 400;
const MIN_CHARS = 3;

export function initTableFilters({ formId, storageKey, ajax = false, targets = [], paginationTargets = [] }) {
  // Use a jQuery event namespace per form to avoid accumulating handlers
  // across Turbolinks navigations
  const ns = `.tableFilter_${formId}`;
  const $form = $(`#${formId}`);
  if (!$form.length) {
    // Clean up any stale handlers bound during a previous Turbolinks navigation
    // where this form existed. Without this, stale handlers would fire AJAX
    // requests to the wrong action (e.g. /customers instead of /customers/service_subscriptions).
    $(document).off(ns);
    return;
  }

  let debounceTimer = null;

  function syncFiltersToForm() {
    $('[data-filter]').each(function () {
      const field = $(this).data('filter');
      $form.find(`[name="${field}"]`).val($(this).val());
    });
  }

  function submitFilters() {
    // Sync visible inputs to hidden form fields
    syncFiltersToForm();

    if (ajax && targets.length) {
      submitAjax();
    } else {
      // Full page reload: remember focus for restoration after reload
      const $focused = $('[data-filter]:focus');
      if ($focused.length) {
        sessionStorage.setItem(storageKey, $focused.data('filter'));
      }
      $form.trigger('submit');
    }
  }

  function submitAjax() {
    var formData = new FormData($form[0]);
    var params = new URLSearchParams();
    // Only add non-empty params for cleaner URL
    formData.forEach(function (value, key) {
      if (value) params.append(key, value);
    });

    var url = $form.attr('action') + '?' + params.toString();

    // Update browser URL without reload
    window.history.replaceState(null, '', url);

    fetch(url, {
      headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (response) { return response.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        targets.forEach(function (selector) {
          var newEl = doc.querySelector(selector);
          var oldEl = document.querySelector(selector);
          if (newEl && oldEl) {
            oldEl.innerHTML = newEl.innerHTML;
          }
        });
        if (typeof Formazione !== 'undefined' && typeof Formazione.setup_on_load === 'function') {
          Formazione.setup_on_load();
        }
      });
  }

  // Remove previous handlers for this form, then re-bind
  $(document).off(`input${ns}`).off(`change${ns}`);

  // Text inputs: debounced, only search with MIN_CHARS or when cleared
  $(document).on(`input${ns}`, `[data-filter]`, function () {
    if (this.tagName === 'SELECT') return;
    clearTimeout(debounceTimer);
    var val = $(this).val().trim();
    if (val.length > 0 && val.length < MIN_CHARS) return;
    debounceTimer = setTimeout(submitFilters, DEBOUNCE_MS);
  });

  // Dropdowns: immediate
  $(document).on(`change${ns}`, `select[data-filter]`, function () {
    clearTimeout(debounceTimer);
    submitFilters();
  });

  // In AJAX mode, intercept pagination link clicks
  var pagTargets = paginationTargets.length ? paginationTargets : targets;
  if (ajax && pagTargets.length) {
    // Disable Turbolinks on pagination containers so it doesn't race with our AJAX handler.
    // Setting the attribute on the container persists across innerHTML replacements.
    pagTargets.forEach(function (selector) {
      var el = document.querySelector(selector);
      if (el) el.setAttribute('data-turbolinks', 'false');
    });

    pagTargets.forEach(function (selector) {
      $(document).off(`click${ns}`, selector + ' a').on(`click${ns}`, selector + ' a', function (e) {
        e.preventDefault();
        var href = $(this).attr('href');
        if (!href) return;

        window.history.replaceState(null, '', href);

        fetch(href, {
          headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        })
          .then(function (response) { return response.text(); })
          .then(function (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');

            targets.forEach(function (sel) {
              var newEl = doc.querySelector(sel);
              var oldEl = document.querySelector(sel);
              if (newEl && oldEl) {
                oldEl.innerHTML = newEl.innerHTML;
              }
            });
            if (typeof Formazione !== 'undefined' && typeof Formazione.setup_on_load === 'function') {
              Formazione.setup_on_load();
            }
          });
      });
    });
  }

  // Restore focus to the last active filter field after page reload
  const savedFilter = sessionStorage.getItem(storageKey);
  if (savedFilter) {
    sessionStorage.removeItem(storageKey);
    const $target = $(`[data-filter="${savedFilter}"]`);
    if ($target.length) {
      const el = $target[0];
      el.focus();
      // Move cursor to end of text (avoid selecting all)
      if (el.type === 'text' && el.value.length) {
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }
  }
}